import React, { useCallback, useEffect, useState, useRef, Suspense } from "react";
import { useDispatch } from "react-redux";
import {
	Redirect,
	Route,
	Switch,
	useHistory,
	useLocation,
	useParams,
	matchPath,
} from "react-router-dom";
import classNames from 'classnames';

import Socket from "../../connection/socket";
import {
	updateUserSessionEndTime,
	loadPage,
	loadEventBundle,
	setBlProfileToken,
	getTicketQuantities,
	setLiveSessions,
	setDisplayProfileModal,
	getPageSession,
	setPageSession,
	updatePageSessionEndTime,
	clearErrorBoundaryComponents,
} from "../../store/actions/event/event-actions";
import { LanguagesAbbr, BrandliveEvent, SqsSocketTypes, IAnnouncement } from "../../types/working-model";
import WaitingIndicator from "../general-ui/waiting-indicator/waiting-indicator";
import Alerts from '../general-ui/alert/alert';
import LiveEventDocumentHead from "./live-event-document-head";
import MarketingPage from "./marketing-page/marketing-page";
import MarketingRegisteredRoutes from "./marketing-page/marketing-registered-routes";
import RegistrationPage from "./registration/registration";
import Session from "./session/session";
import { eventPath, getAttendeeLocalLanguage } from "./utils";
import ScrollToTop from './scrolll-to-top';
import { getStorageItem } from "../../utils/local-storage";
import { trackGoogleAnalytics } from "../../utils/google-analytics";
import useRouteMap from "./hooks/use-route-map";
import { hasEventAccess } from "../../utils/registration-utils";
import { showAlert, showAnnouncement } from "../general-ui/alert/alert-service";
import { storeAllQueryParams, storeReferrer, getStoredReferrer, shouldDisplayLandingPage, getTemplateClassName, isRtlLanguage } from "../../utils/utils";
import useCheckBreakoutBootedStatus from "./hooks/use-check-breakout-booted-status";
import { useTypedSelector } from '../../store/reducers/use-typed-selector';
import { TemplateClassNames, TemplateNames } from "../../types/template-layouts";
import useBundleUpdateSockets from "./use-bundle-update-sockets";
import useCheckUserGating from "./use-check-user-gating";
import TestErrorBoundary from "../../utils/test-error-boundary";
import { OptionalComponent } from "../../utils/optional-component";
import ErrorBoundary, { TEST_ERROR_BOUNDARIES } from "../../utils/error-boundary";
import LocationTracking from "./location-tracking";
import PreviewPasscodeChecker from "./preview-passcode-checker";
import PageNotFound from "../404/404";
import socketManager from "../../connection/socket-main-thread/socket-manager";
import useTrackPageView from "../../utils/tracking/use-track-page-view";

import { useTranslate } from '../../i18n/useTranslationModules';
import CookieBanner from "../general-ui/cookie-banner/cookie-banner";
import CookieBannerStickyWrapper from "../general-ui/cookie-banner/cookie-banner-sticky-wrapper";

const EventChatMain = React.lazy(() => import("./event-chat/event-chat-main"));
const BreakoutRoom = React.lazy(() => import('./modules/breakout-rooms/breakout-room'));
const Fireside = React.lazy(() => import("./session/fireside/fireside"));
const PrivacyPolicy = React.lazy(() => import("./privacy-policy/privacy-policy"));
const TermsConditions = React.lazy(() => import("./terms-and-conditions/terms-and-conditions"));
const NonRegisteredUserForm = React.lazy(() => import("./session/live-chat/non-registered-user-form"));
const ProfileModal = React.lazy(() => import("./profile-page/profile-modal"));
export interface ParamsProps {
	id: string; /** event uuid */
	uuid: string;
	eventName: string;
	tab: string; /** editor nav tab */
	language: LanguagesAbbr;
	sessionUuid: string;
	attendeeId?: string;
	// scheduleUuid?: IScheduledEmail['uuid']; // FUTURE DEV: Keeping for now until we add content editing to emails and decide if we want to use the details page
	/**@deprecated  keeping for backwards compatibility*/
	session: string;
	customPath: string;
	breakoutUuid: string;
	audienceID: string;
}

export interface LiveEventProps {
	debug?: boolean;
	host?: string;
	eventNamePass?: string;
	isEditor: boolean;
}

function Loader() {
	return (
		<div style={{
			position: 'absolute',
			inset: 0,
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
			background: 'white'
		}}>
			<WaitingIndicator />
		</div>
	);
}

export function registrationPath(lang: string, eventName?: string, uuid?: string): string {
	if (eventName) return `${eventName}/${lang}/registration`;
	else return `event/${uuid}/${lang}/registration`;
}

const LiveEvent: React.FC<LiveEventProps> = ({ debug, host, eventNamePass, isEditor }) => {
	const dispatch = useDispatch();
	const history = useHistory();

	const { uuid }: ParamsProps = useParams();
	const location = useLocation();

	const { pathname, search: locationSearch, state } = location;
	const stateReferrer = (state as Record<string, string> | undefined)?.referrer;

	const eventBundle = useTypedSelector(event => event.LiveEventReducer.eventBundle);
	const registrationId = useTypedSelector(event => event.LiveEventReducer.registrationId);
	const loadingEventBundle = useTypedSelector(event => event.LiveEventReducer.loadingEventBundle);
	const translationUpdates = useTypedSelector(event => event.LiveEventReducer.translationUpdates);
	const pageSession = useTypedSelector(event => event.LiveEventReducer.pageSession);
	const gettingPageSession = useTypedSelector(event => event.LiveEventReducer.gettingPageSession);
	const getPageSessionError = useTypedSelector(event => event.LiveEventReducer.getPageSessionError);
	const userSession = useTypedSelector(event => event.LiveEventReducer.userSession);
	const _404 = useTypedSelector(event => event.LiveEventReducer._404);
	const blProfile = useTypedSelector(state => state.LiveEventReducer.blProfileUser);
	const blProfileUserToken = useTypedSelector(state => state.LiveEventReducer.blProfileUserToken);
	const shouldRedirectToRegistration = useTypedSelector(event => event.LiveEventReducer.shouldRedirectToRegistration);
	const playbackUrls = useTypedSelector(event => event.LiveEventReducer.playbackUrls);
	const validSessions = useTypedSelector(event => event.LiveEventReducer.validSessions);
	const finishedRegistering = useTypedSelector(event => event.LiveEventReducer.finishedRegistering);
	const userVerified = useTypedSelector(event => event.LiveEventReducer.userVerified);
	const displayProfile = useTypedSelector(event => event.LiveEventReducer.displayProfileModal);
	const firesidesHostSessions = useTypedSelector(event => event.LiveEventReducer.firesidesHostSessions);

	const eventChatEnabled = eventBundle?.social_settings?.messaging;
	const displayLandingPage = shouldDisplayLandingPage(eventBundle);
	const template = getTemplateClassName(eventBundle?.template.name);
	const templateName = (eventBundle?.template.name) || TemplateNames.Classic;
	const isPreview = eventBundle?.is_preview;

	const [isModalPreview, setIsModalPreview] = useState(isPreview);
	const [canShowLoader, setCanShowLoader] = useState(true);
	const [templateLoaded, setTemplateLoaded] = useState(false);
	const [fetchEventBundle, setFetchEventBundle] = useState<'before' | 'during' | 'complete'>('before');

	// additional socket with language parameter
	const socketL = useRef<Socket | null>(null);

	const { t } = useTranslate(['homepage', 'session']);
	const deferredTranslation = useRef({ t });

	const RouteMap = useRouteMap({ debug });
	const [interacted, setInteracted] = useState(false);
	// Prevents duplicated end session API calls from firing
	const [pageSessionAlreadyExists, setPageSessionAlreadyExists] = useState(false);

	const excludeChat = matchPath(pathname, {
		path: [
			//TODO: put firesides route here when the epic is merged in
			RouteMap.Fireside,
			RouteMap.FiresideNoHomepage,
			RouteMap.Registration,
			RouteMap.Breakout
		],
		exact: true
	});

	const templateFileName = eventBundle?.template?.name?.toLocaleLowerCase().replace(/\s/g, '') ?? '';

	useEffect(() => {
		setIsModalPreview(isPreview);
	}, [isPreview]);

	useEffect(() => {
		async function loadTemplate() {
			// template logic defaults to Apollo, which even if the correct template is imported
			// eventually, it will still import apollo at some point. This was easier than changing the logic for template above
			if (templateFileName) {
				// async import live event template styling
				await import(`../../scss/live-event/${eventBundle?.template.name.toLocaleLowerCase().replace(/\s/g, '')}.scss`);
				setTemplateLoaded(true);
			}
		}
		if (eventBundle?.template && TemplateClassNames[eventBundle.template.name]) {
			loadTemplate();
		} else {
			// the template css will be loaded in the document head, similar to custom css. 
			// We can assume the template is loaded since the document head is only rendered if the event bundle (with the template css) exists
			setTemplateLoaded(true);
		}
	}, [eventBundle?.template, templateFileName]);

	const getLanguage = useCallback(() => getAttendeeLocalLanguage(eventBundle), [eventBundle]);

	const language = getLanguage();

	const shouldBlockSessionPageView = true;  // pulled out into a named variable to give context about what this boolean is controlling
	useTrackPageView(shouldBlockSessionPageView, language);

	useCheckUserGating();

	// if a user is kicked out of a breakout room, show message
	useCheckBreakoutBootedStatus();

	// if storage token then set it in redux
	useEffect(() => {
		const blProfileToken = getStorageItem('Bl-Profile-Token');
		if (blProfileToken) {
			dispatch(setBlProfileToken(blProfileToken));
		}

		trackGoogleAnalytics();
	}, [dispatch]);

	useEffect(() => {
		if (eventBundle) return;

		//REMOVE THIS BLOCK WHEN REMOVING DEPRECATED METHOD
		if (!debug) {
			if (eventNamePass && host) {
				//munging for staging
				dispatch(loadEventBundle(host.replace('brandlive-staging.com', 'brand.live'), eventNamePass));
				dispatch(getTicketQuantities(host.replace('brandlive-staging.com', 'brand.live'), eventNamePass));
			}
		} else if (uuid) {
			dispatch(loadEventBundle('dev', uuid));
			dispatch(getTicketQuantities('dev', uuid));
		}
	}, [uuid, dispatch, debug, host, eventNamePass, eventBundle]);


	useEffect(() => {
		const { t } = deferredTranslation.current;
		if (eventBundle && loadingEventBundle) {
			dispatch(loadPage(eventBundle, t));
		}
	}, [dispatch, eventBundle, loadingEventBundle]);

	useEffect(() => {
		if (loadingEventBundle && fetchEventBundle === 'before') {
			setFetchEventBundle('during');
		}
		if (!loadingEventBundle && fetchEventBundle === 'during') {
			setFetchEventBundle('complete');
		}
	}, [fetchEventBundle, loadingEventBundle]);

	useEffect(() => {
		dispatch(setLiveSessions(playbackUrls));
	}, [playbackUrls, dispatch]);

	useEffect(() => {
		// getPageSessionError prevents this from turning into an infinite loop.
		// if the dispatch(getPageSession...) lambda call fails, then getPageSessionError will be the result string message
		if (blProfile && !gettingPageSession && eventBundle?.event && !getPageSessionError) {
			const storedPageSession = getStorageItem(`pageSession.${eventBundle.event}`);
			if (storedPageSession) {
				dispatch(setPageSession(storedPageSession));
			} else {
				dispatch(getPageSession(eventBundle.event, blProfileUserToken, registrationId ?? undefined, getStoredReferrer(eventBundle?.uuid) ?? undefined));
			}
		}
	}, [blProfile, gettingPageSession, eventBundle, dispatch, blProfileUserToken, getPageSessionError, registrationId]);

	const setupEndPageSessionListeners = useCallback((existingPageSession: string) => {
		const endPageSession = () => {
			dispatch(updatePageSessionEndTime(pageSession));
			navigator.sendBeacon(`${process.env.REACT_APP_HV_HOST}/update-page-session-end-time`, JSON.stringify({ uuid: existingPageSession }));
		};

		const updatePageSessionEndTimeVisibility = () => {
			if (document.visibilityState !== 'hidden') {
				return;
			}

			endPageSession();
		};

		// Not all browsers support visibility change. And some, like safari, just don't fire it when leaving the page.
		// pagehide and (before)unload are less reliable but a good fallback
		document.addEventListener('visibilitychange', updatePageSessionEndTimeVisibility);

		if (typeof window.onpagehide != undefined) {
			window.addEventListener('pagehide', endPageSession);
		} else {
			window.addEventListener('unload', endPageSession);
			window.addEventListener('beforeunload', endPageSession);
		}
	}, [pageSession, dispatch]);

	// Ensures existing page sessions can be ended
	useEffect(() => {
		let existingPageSession: string | undefined;

		for (let i = 0; i < localStorage.length; i++) {
			if (localStorage.key(i)?.includes('pageSession')) {
				existingPageSession = getStorageItem(localStorage.key(i) as string);
			}
		}

		if (!existingPageSession) {
			return;
		}

		setPageSessionAlreadyExists(true);

		return setupEndPageSessionListeners(existingPageSession);
	}, [setPageSessionAlreadyExists, setupEndPageSessionListeners]);

	useEffect(() => {
		if (!pageSession || pageSessionAlreadyExists) {
			return;
		}

		return setupEndPageSessionListeners(pageSession);
	}, [pageSession, setupEndPageSessionListeners, pageSessionAlreadyExists]);

	useEffect(() => {
		if (!userSession) {
			return;
		}

		const updateEndTime = () => {
			dispatch(updateUserSessionEndTime(userSession));
		};

		const updateEndTimeVisibility = () => {
			if (document.visibilityState !== 'hidden') {
				return;
			}

			updateEndTime();
		};

		// Not all browsers support visibility change. And some, like safari, just don't fire it when leaving the page.
		// pagehide and (before)unload are less reliable but a good fallback
		document.addEventListener('visibilitychange', updateEndTimeVisibility);

		if (typeof window.onpagehide != undefined) {
			window.addEventListener('pagehide', updateEndTime);
		} else {
			window.addEventListener('unload', updateEndTime);
			window.addEventListener('beforeunload', updateEndTime);
		}

		return () => {
			dispatch(updatePageSessionEndTime(pageSession));
			dispatch(updateUserSessionEndTime(userSession));
			window.removeEventListener('pagehide', updateEndTime);
			window.removeEventListener('unload', updateEndTime);
			window.removeEventListener('beforeunload', updateEndTime);
			document.removeEventListener('visibilitychange', updateEndTimeVisibility);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [userSession]);

	useBundleUpdateSockets();

	// show announcements in users selected language
	const socketKey = `event-${eventBundle?.uuid}-${language}`;
	useEffect(() => {
		// show announcement as alert
		const handleAnnouncement = ({ data }: { data: Partial<IAnnouncement>; }) => {
			showAnnouncement({ ...data });
		};
		//create socket manually - like above - and remove when done
		if (!socketKey.includes('undefined')) {
			// use the language the user has selected via the profile menu or the browser preferred language with fallback to english
			socketL.current = socketManager.get(socketKey);
			socketL.current.addListener(SqsSocketTypes.ANNOUNCEMENT, handleAnnouncement);
		}
		return () => {
			socketManager.leave(socketKey);
			socketL.current?.removeListener(SqsSocketTypes.ANNOUNCEMENT, handleAnnouncement);
		};
	}, [socketKey]);


	useEffect(() => {
		storeAllQueryParams(locationSearch, eventBundle?.uuid);
		storeReferrer(eventBundle?.uuid);
		//don't think this is even turned on, but in case it is, kill it here.
		if (eventBundle?.channel_settings?.integrations?.google_analytics && eventBundle.channel_settings.integrations.google_analytics.startsWith("UA-")) {
			// window.ga("create", eventBundle.channel_settings.integrations.google_analytics, "auto", `brandlive${eventBundle.uuid}`);
			// window.ga(`brandlive${eventBundle.uuid}.send`, "pageview");
		}
	}, [eventBundle?.channel_settings?.integrations?.google_analytics, eventBundle?.uuid, locationSearch]);

	useEffect(() => {
		if (interacted) return;

		const hasInteracted = () => {
			setInteracted(true);
		};

		window.addEventListener('click', hasInteracted);
		window.addEventListener('touchstart', hasInteracted);

		return () => {
			window.removeEventListener('click', hasInteracted);
			window.removeEventListener('touchstart', hasInteracted);
		};
	}, [interacted]);

	const fadeOutLoader = useCallback(() => {
		setCanShowLoader(false);
	}, []);

	const hasHomepage = !!eventBundle?.homepage;

	const renderRedirect = useCallback(() => {
		if (eventBundle?.homepage) {
			return <Redirect to={stateReferrer ? stateReferrer : `/${eventPath(eventNamePass, uuid)}/${getLanguage()}/home`} />;
		}
		return <Redirect to={stateReferrer ? stateReferrer : `/${eventPath(eventNamePass, uuid)}/${getLanguage()}`} />;
	}, [eventBundle?.homepage, eventNamePass, getLanguage, stateReferrer, uuid]);

	const shouldBypassRegistration = useCallback((eventBundle: BrandliveEvent): boolean => {
		return hasEventAccess(eventBundle, validSessions, finishedRegistering, userVerified, firesidesHostSessions);
	}, [validSessions, finishedRegistering, userVerified, firesidesHostSessions]);

	const handlePageErrorReroute = useCallback((routeOptions: 'home' | 'landing' | 'registration' | 'support' | 'singleSessionPage') => {
		return () => {
			if (window.location.hostname === 'localhost') return;
			let routeTo;
			switch (routeOptions) {
				case 'landing': {
					routeTo = `/${eventPath(eventNamePass, uuid)}/${getLanguage()}`;
					break;
				}
				case 'singleSessionPage': {
					if (eventBundle?.homepage) {
						const singleSessionUuid = eventBundle?.sessions[0].uuid;
						routeTo = `/${eventPath(eventNamePass, uuid)}/${getLanguage()}/session/${singleSessionUuid}`;
					} else {
						routeTo = `/${eventPath(eventNamePass, uuid)}/${getLanguage()}`;
					}
					break;
				}
				case 'home':
				case 'registration': {
					routeTo = `/${eventPath(eventNamePass, uuid)}/${getLanguage()}/${routeOptions}`;
					break;
				}
			}

			if (routeTo) {
				history.push(routeTo);
				showAlert({
					message: "Error loading page",
					description: "Something went wrong on our end, please try again in a few minutes",
					duration: 7000,
					type: "error"
				});
			}

			TEST_ERROR_BOUNDARIES && dispatch(clearErrorBoundaryComponents());
		};
	}, [dispatch, eventBundle?.homepage, eventBundle?.sessions, eventNamePass, getLanguage, history, uuid]);

	const renderRegistrationPage = useCallback(() => {
		if (!eventBundle) return null;
		// if no reg required, redirect to event
		if (shouldBypassRegistration(eventBundle)) {
			// navigate to event
			return renderRedirect();
		}

		// else, go to registration page
		return (
			<ErrorBoundary uniqueLabel="Registration page - Homepage ON Registration ON" onError={handlePageErrorReroute("support")} pageNotFound>
				<RegistrationPage />
			</ErrorBoundary>
		);
	}, [eventBundle, handlePageErrorReroute, renderRedirect, shouldBypassRegistration]);

	const renderEventPages = useCallback(() => {
		if (!eventBundle) return null;

		const bypassRegistration = shouldBypassRegistration(eventBundle);
		// const [SocketContext, SocketProvider] = createCtx()

		// if no reg required, render event pages
		if (bypassRegistration) {
			return (
				<MarketingRegisteredRoutes debug={debug} onRenderError={handlePageErrorReroute} />
			);
		}

		if (displayLandingPage) {
			// if all fails, render landing page
			return (
				<ErrorBoundary uniqueLabel="Landing page - Homepage ON Registration ON" onError={handlePageErrorReroute("support")} pageNotFound>
					<MarketingPage />
				</ErrorBoundary>
			);
		}

		return renderRegistrationPage();
	}, [debug, displayLandingPage, eventBundle, handlePageErrorReroute, renderRegistrationPage, shouldBypassRegistration]);

	useEffect(() => {
		const listener = () => {
			if (window.location.pathname !== location.pathname) {
				history.replace(
					window.location.pathname
				);
			}
		};

		window.addEventListener('popstate', listener);

		return () => {
			window.removeEventListener('popstate', listener);
		};
	}, [history, location.pathname]);

	// these must go after all the hooks
	if (_404 || (fetchEventBundle === 'complete' && !eventBundle?.event)) {
		return (<PageNotFound event />);
	}

	if (!eventBundle || !templateLoaded) {
		return (
			<Loader />
		);
	}

	const isSingleSession = eventBundle.sessions?.length === 1;

	if (isModalPreview) {
		return <PreviewPasscodeChecker setIsModalPreview={setIsModalPreview} />;
	}

	return hasHomepage ? (
		<div className={classNames("live-event-container", template, templateFileName, { "force-rtl": isRtlLanguage(language) })}>
			<LiveEventDocumentHead eventBundle={eventBundle} />
			<ScrollToTop />
			<LocationTracking debug={debug} />
			<Switch>
				<Redirect from="/:url*(/+)" to={pathname.slice(0, -1)} /> {/* Removes trailing slashes */}
				{(shouldRedirectToRegistration) && (
					<Route path={[RouteMap.Home, RouteMap.Landing, RouteMap.Session, RouteMap.Breakout, RouteMap.Fireside]} exact={true}>
						<Redirect to={{
							pathname: `/${eventPath(eventNamePass, uuid)}/${getLanguage()}/registration`,
							state: { referrer: window.location.pathname }
						}} />
					</Route>
				)}
				{(eventBundle.settings.display_sessions === false) && (
					<Route path={[RouteMap.Session, RouteMap.Sessions]} exact={true}>
						<Redirect to={{
							pathname: `/${eventPath(eventNamePass, uuid)}/${getLanguage()}/home`,
							state: { referrer: window.location.pathname }
						}} />
					</Route>
				)}
				<Route path={RouteMap.Breakout}>
					<Suspense fallback={<Loader />}>
						<ErrorBoundary uniqueLabel={`Breakout room page - Homepage ON${eventBundle.registration_on ? " Registration ON" : ''}`} onError={handlePageErrorReroute("home")}>
							<BreakoutRoom />
						</ErrorBoundary>
					</Suspense>
				</Route>
				<Route exact path={RouteMap.Fireside}>
					<ErrorBoundary uniqueLabel={`Fireside stage page - Homepage ON${eventBundle.registration_on ? " Registration ON" : ''}`} onError={handlePageErrorReroute("home")}>
						{eventBundle.sessions?.length > 0 ? (
							<Suspense fallback={<Loader />}>
								<Fireside singleSession={isSingleSession} isOnStage={true} interacted={interacted} isEditor={isEditor} />
							</Suspense>
						) : (
							<Loader />
						)}
					</ErrorBoundary>
				</Route>
				<Route exact path={RouteMap.Session}>
					<ErrorBoundary uniqueLabel={`Session page - Homepage ON Registration${eventBundle.registration_on ? " ON" : " OFF"}`} onError={handlePageErrorReroute("home")}>
						{(eventBundle.registration_on ? (
							<>
								{loadingEventBundle ? (
									<WaitingIndicator />
								) : (
									<>
										{hasEventAccess(eventBundle, validSessions, finishedRegistering, userVerified, firesidesHostSessions) ?
											<Session interacted={interacted} debug={debug} />
											: <Redirect to={{
												pathname: `/${eventPath(eventNamePass, uuid)}/${getLanguage()}/registration`,
												state: { referrer: window.location.pathname }
											}} />
										}
									</>
								)}
							</>
						) :
							<Session interacted={interacted} debug={debug} />
						)}
					</ErrorBoundary>
				</Route>
				<Route path={RouteMap.Registration}>

					{renderRegistrationPage()}
				</Route>
				<Route path={RouteMap.PrivacyPolicy}>
					<Suspense fallback={<Loader />}>
						<ErrorBoundary uniqueLabel="privacy policy page - Homepage ON" onError={handlePageErrorReroute("landing")}>
							<PrivacyPolicy />
						</ErrorBoundary>
					</Suspense>
				</Route>
				<Route path={RouteMap.TermsConditions}>
					<Suspense fallback={<Loader />}>
						<ErrorBoundary uniqueLabel="terms and conditions page - Homepage ON" onError={handlePageErrorReroute("landing")}>
							<TermsConditions />
						</ErrorBoundary>
					</Suspense>
				</Route>
				<Route path={[RouteMap.Home, RouteMap.Sessions, RouteMap.Profile, RouteMap.AttendeeProfile, RouteMap.CustomPage, RouteMap.Directory]}>
					{renderEventPages()}
				</Route>
				<Route path={RouteMap.Landing}>
					{registrationId && (<MarketingRegisteredRoutes debug={debug} onRenderError={handlePageErrorReroute} />)}
					{!registrationId && displayLandingPage && (
						<ErrorBoundary uniqueLabel={`landing page - Homepage ON${eventBundle.registration_on ? " Registration ON" : ''}`} onError={handlePageErrorReroute("support")} pageNotFound>
							<MarketingPage />
						</ErrorBoundary>)}
					{!registrationId && !displayLandingPage && renderRegistrationPage()}
				</Route>
				<Route path={RouteMap.LandingRedirect}>
					<Redirect to={`/${eventPath(eventNamePass, uuid)}/${getLanguage()}`} />
				</Route>
			</Switch>

			<Suspense fallback="">
				<ErrorBoundary uniqueLabel="Non registered user form - Homepage ON">
					<NonRegisteredUserForm
						event_uuid={eventBundle.uuid}
						event={eventBundle.event}
						channel={eventBundle.channel}
					/>
				</ErrorBoundary>
			</Suspense>

			<OptionalComponent display={!!TEST_ERROR_BOUNDARIES}>
				<Suspense fallback="">
					<TestErrorBoundary />
				</Suspense>
			</OptionalComponent>

			{eventChatEnabled && blProfile && blProfileUserToken && !excludeChat && registrationId && (
				<Suspense fallback="">
					<ErrorBoundary uniqueLabel="Event chat - Homepage ON">
						<EventChatMain
							template={templateName}
							eventBundle={eventBundle}
						/>
					</ErrorBoundary>
				</Suspense>
			)}

			{displayProfile ? (
				<Suspense fallback="">
					<ErrorBoundary uniqueLabel="Profile modal - Homepage ON">

						<ProfileModal
							open={true}
							onClose={() => dispatch(setDisplayProfileModal(null))}
							profile={displayProfile}
							template={template}
							eventBundle={eventBundle}
							language={language}
							history={history}
						/>
					</ErrorBoundary>
				</Suspense>
			) : null}

			{
				canShowLoader && (
					<div
						className={classNames("live-event-waiting", { "fade-out": (!loadingEventBundle && translationUpdates > 0) })}
						onTransitionEnd={fadeOutLoader}
					>
						<WaitingIndicator />
					</div>
				)
			}
			<Alerts />

			{/* sticky position on live event was creating extra space at bottom of page, so setting fixed position for live event */}
			<CookieBannerStickyWrapper fixed>
				<CookieBanner language={language} eventName={eventNamePass} />
			</CookieBannerStickyWrapper>
		</div >
	) : (
		<div className={classNames("live-event-container", template, { "force-rtl": isRtlLanguage(language) })}>
			<LiveEventDocumentHead eventBundle={eventBundle} />
			<ScrollToTop />
			<LocationTracking debug={debug} />
			{eventBundle.registration_on ? (
				<Switch>
					<Redirect from="/:url*(/+)" to={pathname.slice(0, -1)} /> {/* Removes trailing slashes */}
					{(shouldRedirectToRegistration) && (
						<Route path={[RouteMap.Landing, RouteMap.Breakout]} exact={true}>
							<Redirect to={`/${registrationPath(getLanguage(), eventNamePass, uuid)}`} />
						</Route>
					)}
					<Route path={RouteMap.Breakout}>
						<Suspense fallback={<Loader />}>
							<ErrorBoundary uniqueLabel="Breakout room page - Homepage OFF Registration ON" onError={handlePageErrorReroute("singleSessionPage")}>
								<BreakoutRoom />
							</ErrorBoundary>
						</Suspense>
					</Route>
					<Route exact path={RouteMap.FiresideNoHomepage}>
						{eventBundle.sessions?.length > 0 ? (
							<Suspense fallback={<Loader />}>
								<ErrorBoundary uniqueLabel="Fireside stage page - Homepage OFF Registration ON" onError={handlePageErrorReroute("singleSessionPage")}>
									<Fireside singleSession={isSingleSession} isOnStage={true} interacted={interacted} isEditor={isEditor} />
								</ErrorBoundary>
							</Suspense>
						) : (
							<Loader />
						)}
					</Route>
					<Route path={RouteMap.Registration}>
						<ErrorBoundary uniqueLabel="Registration page - Homepage OFF" onError={handlePageErrorReroute("support")} pageNotFound>
							<RegistrationPage />
						</ErrorBoundary>
					</Route>
					<Route path={RouteMap.Profile}>
						<MarketingRegisteredRoutes debug={debug} onRenderError={handlePageErrorReroute} />
					</Route>
					<Route exact path={RouteMap.Landing}>
						<ErrorBoundary uniqueLabel="Session page - Homepage OFF Registration ON" onError={handlePageErrorReroute("support")} pageNotFound>
							{loadingEventBundle ? (
								<></>
							) : (
								<>
									{(registrationId) ? (
										<Session singleSession={true} interacted={interacted} debug={debug} />
									) : (
										<>
											<Redirect to={`/${registrationPath(getLanguage(), eventNamePass, uuid)}`} />
										</>
									)}
								</>
							)}
						</ErrorBoundary>
					</Route>
					<Route path={RouteMap.LandingRedirect}>
						<Redirect to={`/${eventPath(eventNamePass, uuid)}/${getLanguage()}`} />
					</Route>
					<Route path={RouteMap.PrivacyPolicy}>
						<Suspense fallback={<Loader />}>
							<ErrorBoundary uniqueLabel="Privacy policy page - Homepage OFF Registration ON" onError={handlePageErrorReroute("registration")}>
								<PrivacyPolicy />
							</ErrorBoundary>
						</Suspense>
					</Route>
					<Route path={RouteMap.TermsConditions}>
						<Suspense fallback={<Loader />}>
							<ErrorBoundary uniqueLabel="Terms and conditions page - Homepage OFF Registration ON" onError={handlePageErrorReroute("registration")}>
								<TermsConditions />
							</ErrorBoundary>
						</Suspense>
					</Route>
				</Switch>
			) : (
				<Switch>
					<Redirect from="/:url*(/+)" to={pathname.slice(0, -1)} /> {/* Removes trailing slashes */}
					<Route path={RouteMap.Breakout}>
						<ErrorBoundary uniqueLabel="Breakout room page - Homepage OFF Registration OFF" onError={handlePageErrorReroute("singleSessionPage")}>
							<Suspense fallback={<Loader />}>
								<BreakoutRoom />
							</Suspense>
						</ErrorBoundary>
					</Route>
					<Route path={RouteMap.Landing}>
						<ErrorBoundary uniqueLabel="Session page - Homepage OFF Registration OFF" onError={handlePageErrorReroute("support")} pageNotFound>
							<Session singleSession={true} interacted={interacted} debug={debug} />
						</ErrorBoundary>
					</Route>
					<Route exact path={RouteMap.Fireside}>
						<ErrorBoundary uniqueLabel="Fireside stage - Homepage OFF Registration OFF" onError={handlePageErrorReroute("singleSessionPage")}>
							{eventBundle.sessions?.length > 0 ? (
								<Suspense fallback={<Loader />}>
									<Fireside singleSession={isSingleSession} isOnStage={true} interacted={interacted} isEditor={isEditor} />
								</Suspense>
							) : (
								<Loader />
							)}
						</ErrorBoundary>
					</Route>
					<Route path={RouteMap.LandingRedirect}>
						<Redirect to={`/${eventPath(eventNamePass, uuid)}/${getLanguage()}`} />
					</Route>
					<Route path={RouteMap.PrivacyPolicy}>
						<Suspense fallback={<Loader />}>
							<ErrorBoundary uniqueLabel="Privacy policy page - Homepage OFF Registration OFF" onError={handlePageErrorReroute("singleSessionPage")}>
								<PrivacyPolicy />
							</ErrorBoundary>
						</Suspense>
					</Route>
					<Route path={RouteMap.TermsConditions}>
						<Suspense fallback={<Loader />}>
							<ErrorBoundary uniqueLabel="Terms and conditions page - Homepage OFF Registration OFF" onError={handlePageErrorReroute("singleSessionPage")}>
								<TermsConditions />
							</ErrorBoundary>
						</Suspense>
					</Route>
				</Switch>
			)}
			{displayProfile ? (
				<Suspense fallback="">
					<ErrorBoundary uniqueLabel="Profile modal - Homepage OFF">
						<ProfileModal
							open={true}
							onClose={() => dispatch(setDisplayProfileModal(null))}
							profile={displayProfile}
							template={template}
							eventBundle={eventBundle}
							language={language}
							history={history}
						/>
					</ErrorBoundary>
				</Suspense>
			) : null}

			{eventBundle && eventChatEnabled && blProfile && blProfileUserToken && !excludeChat && registrationId && (
				<Suspense fallback="">
					<ErrorBoundary uniqueLabel="Event chat - Homepage OFF">
						<EventChatMain
							template={templateName}
							eventBundle={eventBundle}
						/>
					</ErrorBoundary>
				</Suspense>
			)}

			<Suspense fallback="">
				<ErrorBoundary uniqueLabel="Non-registered user modal - Homepage OFF">
					<NonRegisteredUserForm
						event_uuid={eventBundle.uuid}
						event={eventBundle.event}
						channel={eventBundle.channel}
					/>
				</ErrorBoundary>
			</Suspense>
			<OptionalComponent display={!!TEST_ERROR_BOUNDARIES}>
				<Suspense fallback="">
					<TestErrorBoundary />
				</Suspense>
			</OptionalComponent>

			{canShowLoader && (
				<div
					className={classNames("live-event-waiting", { "fade-out": (!loadingEventBundle && translationUpdates > 0) })}
					onTransitionEnd={fadeOutLoader}
				>
					<WaitingIndicator />
				</div>
			)}
			<Alerts />

			{/* sticky position on live event was creating extra space at bottom of page, so setting fixed position for live event */}
			<CookieBannerStickyWrapper fixed>
				<CookieBanner language={language} eventName={eventNamePass} />
			</CookieBannerStickyWrapper>
		</div>
	);
};

export default LiveEvent;